import React from 'react';

export interface CommonsTreeLeafRow {
  key: string;
  label: string;
  checked: boolean;
  /** Optional async; errors should be handled by the caller. */
  onCheckedChange: (nextChecked: boolean) => void | Promise<void>;
  disabled?: boolean;
  /** Resalta la etiqueta en negrita (permiso activado respecto a la última carga del backend). */
  labelBold?: boolean;
  /** Etiqueta en cursiva (permiso quitado respecto a la última carga del backend). */
  labelItalic?: boolean;
}

export interface CommonsTreeBranch {
  key: string;
  title: string;
  rows: CommonsTreeLeafRow[];
  defaultExpanded?: boolean;
  /** Checkbox en la cabecera del módulo (rama). */
  moduleCheckbox?: {
    checked: boolean;
    onChange: (nextChecked: boolean) => void;
    disabled?: boolean;
    /** Opacidad del checkbox cuando la selección no está al 100% (ej. `0.6`). Omite para 1. */
    checkboxOpacity?: number;
  };
}

export interface CommonsTreeProps {
  branches: CommonsTreeBranch[];
  loading?: boolean;
  error?: string | null;
  /** Section title above the tree */
  legend?: string;
  accentColor?: string;
}

/**
 * Árbol reusable: ramas colapsables (`<details>`), hojas con checkbox + etiqueta.
 */
export default function CommonsTree({
  branches,
  loading,
  error,
  legend = 'Permisos',
  accentColor = '#6366f1',
}: CommonsTreeProps) {
  return (
    <div
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        background: '#fff',
        padding: '16px 18px',
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 12 }}>{legend}</div>
      {loading && <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>Cargando árbol…</p>}
      {error && (
        <p style={{ margin: '0 0 8px', color: '#b91c1c', fontSize: 13 }}>{error}</p>
      )}
      {!loading && !branches.length && !error && (
        <p style={{ margin: 0, color: '#94a3b8', fontSize: 13 }}>No hay datos para mostrar.</p>
      )}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {branches.map(branch => (
          <li key={branch.key} style={{ marginBottom: 8 }}>
            <details open={branch.defaultExpanded !== false}>
              <summary
                style={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                  color: '#334155',
                  padding: '6px 0',
                  listStyle: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {branch.moduleCheckbox && (
                  <input
                    type="checkbox"
                    checked={branch.moduleCheckbox.checked}
                    disabled={branch.moduleCheckbox.disabled}
                    style={{
                      accentColor,
                      marginTop: 0,
                      flexShrink: 0,
                      opacity: branch.moduleCheckbox.checkboxOpacity ?? 1,
                    }}
                    aria-label={`Seleccionar módulo ${branch.title}`}
                    onClick={e => e.stopPropagation()}
                    onChange={e => {
                      e.stopPropagation();
                      branch.moduleCheckbox!.onChange(e.target.checked);
                    }}
                  />
                )}
                <span style={{ color: accentColor }} aria-hidden>
                  ▸
                </span>
                <span>{branch.title}</span>
              </summary>
              <ul
                style={{
                  listStyle: 'none',
                  margin: '4px 0 0',
                  padding: '8px 0 4px 20px',
                  borderLeft: `2px solid ${accentColor}33`,
                }}
              >
                {branch.rows.map(row => (
                  <li key={row.key} style={{ marginBottom: 6 }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 8,
                        cursor: row.disabled ? 'not-allowed' : 'pointer',
                        fontSize: 13,
                        color: '#475569',
                        lineHeight: 1.4,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={row.checked}
                        disabled={row.disabled}
                        style={{ marginTop: 2, accentColor }}
                        onChange={e => {
                          const next = e.target.checked;
                          void Promise.resolve(row.onCheckedChange(next));
                        }}
                      />
                      <span
                        style={{
                          ...(row.labelBold ? { fontWeight: 700 as const } : {}),
                          ...(row.labelItalic ? { fontStyle: 'italic' as const } : {}),
                        }}
                      >
                        {row.label}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </details>
          </li>
        ))}
      </ul>
      <style>{`
        .commons-tree details > summary::-webkit-details-marker { display: none; }
      `}</style>
    </div>
  );
}
